import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

export interface UseDebouncedSearchResult {
    /** Mirrors the controlled `value` immediately, for responsive typing feedback. */
    localValue: string;
    /** Wire directly to an `Input`'s `onChange`. */
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * EAA-1666: `TableToolbar` and `ListFilters` each independently reimplemented
 * this exact search-debounce pattern (byte-for-byte identical logic) — build
 * it once here instead of porting the duplication into `packages/ui`.
 */
export function useDebouncedSearch(
    value: string | undefined,
    onChange: ((value: string) => void) | undefined,
    delay = 300
): UseDebouncedSearchResult {
    const [localValue, setLocalValue] = useState(value ?? '');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    // EAA-1666: keep the latest `onChange` in a ref rather than a `useMemo`
    // dep. Callers commonly pass an inline `onSearchChange={(v) => ...}`, so
    // depending on its identity recreated (and cancelled, see below) the
    // debounced instance on every unrelated parent re-render — a pending
    // keystroke inside the debounce window could get silently dropped.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const debouncedSearch = useMemo(
        () =>
            debounce((next: string) => {
                onChangeRef.current?.(next);
            }, delay),
        [delay]
    );

    // Cancel only when the debounced instance itself goes away (delay
    // change or unmount) — not on every `onChange` identity change.
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const next = e.target.value;
            setLocalValue(next);
            debouncedSearch(next);
        },
        [debouncedSearch]
    );

    return { localValue, handleChange };
}
